import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { ChevronDown, ExternalLink } from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { COLLECTIONS, setPublishStatus } from "../utils/cmsSchema";
import { resolveUniqueSlug } from "../utils/slugUniqueness";
import { slugify } from "../utils/slugify";
import { revalidateFor } from "../utils/revalidatePublic";
import FormShell, {
  FormSection,
  PublishToggle,
  TextArea,
  TextField,
} from "../components/FormShell";
import RichTextEditor from "../components/RichTextEditor";

// Dedicated form for the `careers` collection. Follows the same layout
// as ArticleForm (blogs/news) but with careers-specific fields:
// department, employmentType, location, applyEmail (per-job override
// vs. the global default in CareersGlobalSettings), applyUrl, pinned.
//
// Rich text lives in `fullDescription` (not `content`, which is the
// blog/news convention) so the schema stays explicit. The short blurb
// on the card is `shortDescription` — separate field, not derived,
// because the accordion card and the detail page use different lengths.

const SCHEMA = COLLECTIONS.careers;

const EMPLOYMENT_TYPES = [
  "Full-time",
  "Part-time",
  "Contract",
  "Internship",
  "Temporary",
];

const DEPARTMENTS = [
  "Sales",
  "Marketing",
  "Research",
  "Operations",
  "Finance",
  "IT",
  "Human Resources",
  "Legal",
  "Executive",
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function CareerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);

  const isNew = !id;
  const docId = useMemo(() => id || v4(), [id]);

  const [form, setForm] = useState({
    title: "",
    shortDescription: "",
    fullDescription: "",
    department: "",
    employmentType: "Full-time",
    location: "",
    postedAt: todayIso(),
    applyEmail: "",
    applyUrl: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    pinned: false,
    published: false,
  });
  const [originalDoc, setOriginalDoc] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [seoOpen, setSeoOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const trackUpload = (b) =>
    setUploadingCount((c) => Math.max(0, c + (b ? 1 : -1)));

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      const data = await getDocData(SCHEMA.key, id);
      if (!active) return;
      if (!data) {
        toast.error("Not found.");
        navigate(SCHEMA.route);
        return;
      }
      setOriginalDoc(data);
      setForm({
        title: data.title || "",
        shortDescription: data.shortDescription || "",
        fullDescription: data.fullDescription || "",
        department: data.department || "",
        employmentType: data.employmentType || "Full-time",
        location: data.location || "",
        postedAt: data.postedAt
          ? String(data.postedAt).slice(0, 10)
          : todayIso(),
        applyEmail: data.applyEmail || "",
        applyUrl: data.applyUrl || "",
        slug: data.slug || "",
        metaTitle: data.metaTitle || "",
        metaDescription: data.metaDescription || "",
        pinned: data.pinned === true,
        published: data[SCHEMA.statusField] === SCHEMA.publishedValue,
      });
      setSlugTouched(!!data.slug);
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (slugTouched) return;
    if (!form.title) return;
    setForm((p) => ({ ...p, slug: slugify(form.title) }));
  }, [form.title, slugTouched]);

  const handleSave = async (publishOverride) => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (uploadingCount > 0) {
      toast.error("Wait for uploads to finish.");
      return;
    }
    setIsSaving(true);
    try {
      const finalSlug = await resolveUniqueSlug({
        collectionName: SCHEMA.key,
        desiredSlug: form.slug,
        fallbackTitle: form.title,
        ignoreId: docId,
      });
      const nowIso = new Date().toISOString();
      const published =
        typeof publishOverride === "boolean" ? publishOverride : form.published;
      const trimmedTitle = form.title.trim();
      const payload = {
        ...(originalDoc || {}),
        title: trimmedTitle,
        titleLower: trimmedTitle.toLowerCase(),
        shortDescription: form.shortDescription.trim(),
        // `description` mirrors shortDescription so ContentCard's
        // preview line renders (that component reads `doc.description`).
        description: form.shortDescription.trim(),
        fullDescription: form.fullDescription || "",
        department: form.department.trim(),
        employmentType: form.employmentType,
        location: form.location.trim(),
        postedAt: form.postedAt,
        applyEmail: form.applyEmail.trim(),
        applyUrl: form.applyUrl.trim(),
        slug: finalSlug,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        pinned: form.pinned,
        updatedAt: nowIso,
        lastUpdatedBy: currentUser?.email || null,
      };
      setPublishStatus(payload, SCHEMA, published);
      if (isNew) {
        payload.createdAt = nowIso;
        payload.createdBy = currentUser?.email || null;
        payload.type = SCHEMA.key;
      }
      await setDocData(SCHEMA.key, docId, payload);
      revalidateFor(SCHEMA.key);
      toast.success(published ? "Job posting published." : "Job posting saved.");
      setForm((p) => ({ ...p, slug: finalSlug, published }));
      if (isNew) {
        navigate(`${SCHEMA.route}/${docId}`, { replace: true });
      } else {
        setOriginalDoc(payload);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  const saveDisabled = isSaving || uploadingCount > 0;
  const publicUrl = `https://www.leechiu.com/careers/${form.slug || docId}`;

  return (
    <FormShell
      backTo={SCHEMA.route}
      backLabel="Back to Careers"
      eyebrow={isNew ? "New Job Posting" : "Editing Job Posting"}
      title={form.title || "Untitled Job Posting"}
      belowTitle={
        !isNew ? (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-leechiuBlue hover:underline"
          >
            View public page
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={() => navigate(SCHEMA.route)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {form.published ? "Update & Keep Published" : "Publish"}
          </button>
        </>
      }
    >
      <div className="lg:col-span-2 space-y-6">
        <FormSection
          title="Role details"
          description="Shown on the accordion card and the individual detail page."
        >
          <TextField
            label="Job title"
            required
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="e.g. Senior Commercial Leasing Broker"
          />
          <TextArea
            label="Short description"
            rows={3}
            value={form.shortDescription}
            onChange={(e) => setField("shortDescription", e.target.value)}
            placeholder="1-2 sentences shown on the /careers card before the reader expands the accordion."
            hint="Recommended: 100–200 characters."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="cms-eyebrow block mb-1.5 text-gray-600">
                Department
              </span>
              <select
                value={form.department}
                onChange={(e) => setField("department", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
              >
                <option value="">— Select —</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="cms-eyebrow block mb-1.5 text-gray-600">
                Employment type
              </span>
              <select
                value={form.employmentType}
                onChange={(e) => setField("employmentType", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
              >
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
              placeholder="e.g. BGC, Makati, Cebu, Remote, Hybrid"
            />
            <TextField
              label="Posted date"
              type="date"
              value={form.postedAt}
              onChange={(e) => setField("postedAt", e.target.value)}
            />
          </div>
        </FormSection>

        <FormSection
          title="Full description"
          description="Rich content — headings, lists, and links supported. Shown when the accordion expands and on the individual /careers/<slug> page."
        >
          <RichTextEditor
            value={form.fullDescription}
            onChange={(html) => setField("fullDescription", html)}
            collection={SCHEMA.key}
            docId={docId}
            placeholder="Tell candidates about the role, responsibilities, and what you're looking for…"
            onUploadingChange={trackUpload}
          />
        </FormSection>

        <FormSection
          title="Application"
          description="Where candidates apply. Leave both blank to fall back to the global apply email set on the Careers home."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Apply email (override)"
              type="email"
              value={form.applyEmail}
              onChange={(e) => setField("applyEmail", e.target.value)}
              placeholder="Leave blank to use the global default"
              hint="Powers the mailto: link on the Apply Now button."
            />
            <TextField
              label="External apply URL (optional)"
              type="url"
              value={form.applyUrl}
              onChange={(e) => setField("applyUrl", e.target.value)}
              placeholder="https://linkedin.com/jobs/…"
              hint="If set, an 'Apply externally' button appears alongside Apply Now."
            />
          </div>
        </FormSection>

        <FormSection
          title={
            <button
              type="button"
              onClick={() => setSeoOpen((s) => !s)}
              className="flex items-center gap-1 text-sm font-semibold text-leechiuBlue"
            >
              SEO &amp; URL
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  seoOpen ? "" : "-rotate-90"
                }`}
              />
            </button>
          }
          description="Controls the public URL, browser tab title, and search-result snippet."
        >
          {seoOpen ? (
            <>
              <TextField
                label="Job posting URL"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", e.target.value);
                }}
                onBlur={(e) => {
                  const formatted = slugify(e.target.value);
                  if (formatted !== e.target.value) {
                    setField("slug", formatted);
                  }
                }}
                placeholder="auto-generated-from-title"
                hint="Paste any phrase; formatted on blur. Leave blank to auto-generate. Uniqueness checked on save."
              />
              <TextField
                label="Page Title"
                value={form.metaTitle}
                onChange={(e) => setField("metaTitle", e.target.value)}
                placeholder="Shown in browser tab and search results"
                hint="Recommended: 50–60 characters. Falls back to the job title if blank."
              />
              <TextArea
                label="Meta Description"
                rows={3}
                value={form.metaDescription}
                onChange={(e) => setField("metaDescription", e.target.value)}
                placeholder="A short summary for search results and social shares"
                hint="Recommended: 140–160 characters. Falls back to short description."
              />
            </>
          ) : null}
        </FormSection>
      </div>

      <div className="space-y-6">
        <FormSection title="Visibility">
          <PublishToggle
            value={form.published}
            onChange={(v) => setField("published", v)}
          />
        </FormSection>

        <FormSection title="Promotion" description="Boost this role's placement.">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 ring-1 ring-gray-100">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-leechiuBlue">
                Feature this role
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Pinned roles appear first on /careers with a Featured badge.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setField("pinned", !form.pinned)}
              role="switch"
              aria-checked={form.pinned}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                form.pinned ? "bg-leechiuOrange" : "bg-gray-300"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition mt-0.5 ${
                  form.pinned ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </FormSection>
      </div>
    </FormShell>
  );
}
